import React from 'react'
import { ChevronRight, Check, Ban, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface OrderStageProgressProps {
  currentStage: string
  onStageChange: (newStage: string) => void
  disabled?: boolean
  /** Orders/Admin may revert a Processed order back to Pending (not Draft). */
  canRevertProcessed?: boolean
}

// Forward-only order flow: Draft → Pending → Processed. "Approved" was retired from
// the flow (kept only so legacy Approved orders still rank/render). Processed and
// Denied are terminal — once there, the order is locked.
const RANK: Record<string, number> = { Draft: 0, Pending: 1, Approved: 2, Processed: 3 }
const TERMINAL = ['Processed', 'Denied']
// Stages a NEW status change can move TO (Approved is not a settable target anymore).
const FORWARD_TARGETS = ['Pending', 'Processed']

/**
 * The statuses an order may move to from `current`, enforcing forward-only progress,
 * no re-entry into the retired "Approved", and locked terminal states. Shared by the
 * stepper, the edit-form status dropdown, and mirrored by the backend PUT guard.
 */
export function allowedNextStatuses(current: string, canRevertProcessed = false): string[] {
  // Authorized roles may revert a Processed order back to Pending (not Draft).
  if (current === 'Processed' && canRevertProcessed) return ['Pending']
  if (TERMINAL.includes(current)) return [] // locked
  const currentRank = RANK[current] ?? 0
  const forward = FORWARD_TARGETS.filter((s) => (RANK[s] ?? 0) > currentRank)
  return [...forward, 'Denied']
}

const ORDER_STAGES = ['Draft', 'Pending', 'Approved', 'Processed', 'Denied'] as const
type OrderStage = (typeof ORDER_STAGES)[number]

export function OrderStageProgress({
  currentStage,
  onStageChange,
  disabled = false,
  canRevertProcessed = false,
}: OrderStageProgressProps) {
  const allowed = allowedNextStatuses(currentStage, canRevertProcessed)
  const isLocked = TERMINAL.includes(currentStage)
  const canRevert = currentStage === 'Processed' && canRevertProcessed

  // Hide the retired "Approved" chip unless this (legacy) order is currently in it.
  const visibleStages: OrderStage[] = ORDER_STAGES.filter(
    (s) => s !== 'Approved' || currentStage === 'Approved'
  )

  const canClickStage = (stage: OrderStage) =>
    !disabled && stage !== currentStage && allowed.includes(stage)

  const handleStageClick = (stage: OrderStage) => {
    if (!canClickStage(stage)) return
    onStageChange(stage)
  }

  const handleKeyDown = (e: React.KeyboardEvent, stage: OrderStage) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleStageClick(stage)
    }
  }

  // Next stage on the main path (Draft → Pending → Processed); skips Approved.
  const getNextStage = (): OrderStage | null => {
    if (isLocked) return null
    const currentRank = RANK[currentStage] ?? 0
    const next = FORWARD_TARGETS.filter((s) => (RANK[s] ?? 0) > currentRank)[0]
    return (next as OrderStage) || null
  }
  const nextStage = getNextStage()

  const getStageStyles = (stage: OrderStage): string => {
    const isActive = stage === currentStage
    const clickable = canClickStage(stage)

    // Denied is always raspberry (destructive); Approved/Processed are green (success).
    if (stage === 'Denied') {
      if (isActive)
        return 'bg-destructive/10 text-destructive border-destructive ring-2 ring-destructive'
      return clickable
        ? 'bg-white text-destructive border-destructive/40 hover:bg-destructive/5 cursor-pointer'
        : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
    }
    if (stage === 'Approved' || stage === 'Processed') {
      if (isActive) return 'bg-success/10 text-success border-success ring-2 ring-success'
      return clickable
        ? 'bg-white text-success border-success/40 hover:bg-success/5 cursor-pointer'
        : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
    }
    // Draft / Pending
    if (isActive) return 'bg-primary/10 text-primary border-primary ring-2 ring-primary'
    return clickable
      ? 'bg-white text-primary border-primary/30 hover:bg-primary/5 cursor-pointer'
      : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
  }

  return (
    <nav
      className="bg-white border rounded-lg p-4 shadow-sm"
      aria-label="Order stage progression"
      role="navigation"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {visibleStages.map((stage, index) => {
            const isActive = stage === currentStage
            const isPast = (RANK[currentStage] ?? -1) > (RANK[stage] ?? 99) && stage !== 'Denied'
            const clickable = canClickStage(stage)

            return (
              <React.Fragment key={stage}>
                <button
                  onClick={() => handleStageClick(stage)}
                  onKeyDown={(e) => handleKeyDown(e, stage)}
                  disabled={!clickable}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
                    ${getStageStyles(stage)}
                  `}
                  aria-label={`${stage} stage${isActive ? ' (current)' : ''}${clickable ? ' - click to change' : ''}`}
                  aria-current={isActive ? 'step' : undefined}
                  tabIndex={clickable ? 0 : -1}
                >
                  {isPast && <Check className="w-4 h-4" aria-hidden="true" />}
                  {stage === 'Denied' && <Ban className="w-4 h-4" aria-hidden="true" />}
                  <span className="font-medium">{stage}</span>
                </button>
                {index < visibleStages.length - 1 && (
                  <ChevronRight
                    className="w-5 h-5 text-gray-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            )
          })}
        </div>

        {nextStage && (
          <Button
            onClick={() => handleStageClick(nextStage)}
            disabled={disabled}
            variant="default"
            className="flex-shrink-0"
            aria-label={`Mark status as ${nextStage}`}
          >
            <Check className="h-4 w-4 mr-2" aria-hidden="true" />
            Mark Status as {nextStage}
          </Button>
        )}

        {canRevert && (
          <Button
            onClick={() => onStageChange('Pending')}
            disabled={disabled}
            variant="outline"
            className="flex-shrink-0"
            aria-label="Revert order to Pending"
          >
            <Undo2 className="h-4 w-4 mr-2" aria-hidden="true" />
            Revert to Pending
          </Button>
        )}
      </div>

      <div className="sr-only">
        Order stage progression. Current stage: {currentStage}.
        {isLocked
          ? canRevert
            ? ' This order is processed. You can revert it to Pending to re-open it.'
            : ' This order is locked and its status can no longer be changed.'
          : ' Orders move forward only and cannot return to an earlier stage.'}
      </div>
    </nav>
  )
}
